import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  adjustFromTimeZoneSafely,
  adjustToTimeZoneSafely,
  AllocatableLoadSettings,
  AllocatableLoadSettingsEntity,
  allocatableLoadSettingsSelector,
  useGetAllocatableLoadSettingsQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  AllocatableLoadSettingsForm,
  AllocatableLoadSettingsFormValues
} from "../../components/form/AllocatableLoadSettingsForm/AllocatableLoadSettingsForm";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {AppContextType, useApp} from "../../providers/RegionProvider/RegionProvider";
import {useAllocatableLoadSettingsMutation} from "../../hooks/useAlloctableLoadSettingsMutation";
import {ErrorPage} from "../../components/page/ErrorPage/ErrorPage";
import {useDisclosure} from "@mantine/hooks";
import {
  AllocatableLoadSettingsPropagationFormModal,
  AllocatableLoadSettingsPropagationFormValues
} from "../../components/form/AllocatableLoadSettingsPropagationForm/AllocatableLoadSettingsPropagationForm";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: Partial<AllocatableLoadSettingsFormValues>
  objectType?: string;
  objectId?: string;
}

export const AllocatableLoadSettingsFormPageRoute = () => {
  const {state} = useLocation();
  const [searchParams] = useSearchParams();

  const idFromState = state?.id;
  const idFromQuery = searchParams.get("id");

  const id = idFromState ?? idFromQuery ?? undefined;

  const objectType = !id ? searchParams.get("objectType") ?? undefined : undefined;
  const objectId = !id ? searchParams.get("objectId") ?? undefined : undefined;

  const copy = state?.copy;
  const formDefaults = state?.formDefaults;

  return (
    <AllocatableLoadSettingsFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
      objectType={objectType}
      objectId={objectId}
    />
  )
};

export const toFormDefaultsFromAllocatableLoadSettings = (
  allocatableLoadSettings: AllocatableLoadSettingsEntity | undefined,
  appContext: AppContextType | undefined
): Partial<AllocatableLoadSettingsFormValues> => {
  return {
    loadVolume: allocatableLoadSettings?.loadVolume,
    loadVolumePct: allocatableLoadSettings?.loadVolumePct,
    shortVolume: allocatableLoadSettings?.shortVolume,
    volumeUnitOfMeasure: allocatableLoadSettings?.volumeUnitOfMeasure ?? appContext?.uoms?.volume,
    minContainerLevel: allocatableLoadSettings?.minContainerLevel,
    minContainerLactLevel: allocatableLoadSettings?.minContainerLactLevel,
    maxNonPrimaryLevel: allocatableLoadSettings?.maxNonPrimaryLevel,
    linearUnitOfMeasure: allocatableLoadSettings?.linearUnitOfMeasure ?? appContext?.uoms?.linear,
    method: allocatableLoadSettings?.method,
    volumeSpreadFactor: allocatableLoadSettings?.volumeSpreadFactor,
    category: allocatableLoadSettings?.category,
    description: allocatableLoadSettings?.description,
    displayName: allocatableLoadSettings?.displayableName,
    effectiveDate: adjustFromTimeZoneSafely(allocatableLoadSettings?.effectiveDttm),
  };
};

export const AllocatableLoadSettingsFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
    objectType,
    objectId,
  } = props;

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);
  const appContext = useApp()

  const [propagate, setPropagate] = useState<AllocatableLoadSettingsEntity | undefined>(undefined);
  const propagateModalDisclosure = useDisclosure(false);
  const propagateModalOpened = propagateModalDisclosure[0]
  const propagateModalHandlers = propagateModalDisclosure[1]

  const selectAllocatableLoadSettings = useMemo(() => {
    return allocatableLoadSettingsSelector()
  }, [])

  const {currAllocatableLoadSettings, isLoading} = useGetAllocatableLoadSettingsQuery(id ?? skipToken, {
    selectFromResult: (result) => ({
      currAllocatableLoadSettings: selectAllocatableLoadSettings(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: Partial<AllocatableLoadSettingsFormValues> = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromAllocatableLoadSettings(currAllocatableLoadSettings, appContext);
  }, [currAllocatableLoadSettings, formDefaults, appContext]);

  const isEdit = useMemo(() => {
    return currAllocatableLoadSettings && !copy
  }, [currAllocatableLoadSettings, copy])

  const title: string = useMemo(() => {
    if (currAllocatableLoadSettings && !copy) {
      return t("page.allocatableloadsettings.form.title.edit", "Edit Allocatable Load Settings", {ns: "common"});
    } else {
      return t("page.allocatableloadsettings.form.title.new", "New Allocatable Load Settings", {ns: "common"});
    }
  }, [currAllocatableLoadSettings, copy, t]);

  const {
    createAllocatableLoadSettings,
    updateAllocatableLoadSettings,
    propagateAllocatableLoadSettings,
    isCreating,
    isUpdating,
    isPropagating,
  } = useAllocatableLoadSettingsMutation()

  const onAllocatableLoadSettingsSubmit = useCallback((formValues: AllocatableLoadSettingsFormValues) => {
    const patch: Partial<AllocatableLoadSettings> = {
      loadVolume: formValues.loadVolume,
      loadVolumePct: formValues.loadVolumePct,
      shortVolume: formValues.shortVolume,
      volumeUnitOfMeasure: formValues.volumeUnitOfMeasure,
      minContainerLevel: formValues.minContainerLevel,
      minContainerLactLevel: formValues.minContainerLactLevel,
      maxNonPrimaryLevel: formValues.maxNonPrimaryLevel,
      linearUnitOfMeasure: formValues.linearUnitOfMeasure,
      method: formValues.method.code,
      volumeSpreadFactor: formValues.volumeSpreadFactor,
      category: formValues.category.code,
      description: formValues.description,
      displayName: formValues.displayName,
      effectiveDttm: adjustToTimeZoneSafely(formValues.effectiveDate)?.getTime(),
    }

    if (isEdit) {
      const allocatableLoadSettings: Partial<AllocatableLoadSettings> = {
        ...currAllocatableLoadSettings.asTypeValue(),
        ...patch
      };
      updateAllocatableLoadSettings(allocatableLoadSettings, {
        onSuccess: (settings) => {
          if (settings.objectType !== "REGION") {
            navigate(-1)
          } else {
            setPropagate(AllocatableLoadSettingsEntity.from(settings))
            propagateModalHandlers.open()
          }
        }
      })
    } else {
      const allocatableLoadSettings: Partial<AllocatableLoadSettings> = {
        objectId: objectId,
        objectType: objectType,
        ...patch,
      };
      createAllocatableLoadSettings(allocatableLoadSettings, {
        onSuccess: (settings) => {
          if (settings.objectType !== "REGION") {
            navigate(-1)
          } else {
            setPropagate(AllocatableLoadSettingsEntity.from(settings))
            propagateModalHandlers.open()
          }
        }
      })
    }
  }, [isEdit, currAllocatableLoadSettings, updateAllocatableLoadSettings, navigate, objectId, objectType, createAllocatableLoadSettings, propagateModalHandlers]);

  const onPropagateModalClose = useCallback(() => {
    setPropagate(undefined);
    propagateModalHandlers.close();
    navigate(-1);
  }, [navigate, propagateModalHandlers]);

  const onPropagateModalSubmit = useCallback((values: AllocatableLoadSettingsPropagationFormValues) => {
    if (!propagate?.id) {
      onPropagateModalClose();
      return;
    }

    propagateAllocatableLoadSettings(propagate.id, {fields: values.fields}, {
      onSuccess: () => {
        onPropagateModalClose();
      }
    })
  }, [onPropagateModalClose, propagate?.id, propagateAllocatableLoadSettings]);

  if (!isLoading && id && !currAllocatableLoadSettings && !objectType && !objectId) {
    return <ErrorPage message="Allocatable Load Settings not found."/>;
  }

  return (
    <ThemedPage>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <AllocatableLoadSettingsForm
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onAllocatableLoadSettingsSubmit}/>
              </Paper>
            )}
          </Center>

          {propagate &&
            <AllocatableLoadSettingsPropagationFormModal
              settings={propagate}
              opened={propagateModalOpened}
              onClose={onPropagateModalClose}
              onSubmit={onPropagateModalSubmit}
              submitting={isPropagating}/>
          }
        </Container>
      )}
    </ThemedPage>
  );
};

